import React, { PropsWithChildren, ReactNode } from 'react'
import { Switch, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { getClassNames } from '../../../_utils/classnames'
import { InfoTooltip } from '../../info-tooltip/info-tooltip'

type AppMessageContentBlockProps = {
    label: ReactNode
    /** Explanatory copy shown on an info icon beside the label. */
    info?: ReactNode
    enabled: boolean
    onEnabledChange: (enabled: boolean) => void
    /** Per-field controls — formatting, colour, position — shown at the row's end. */
    toolbar?: ReactNode
    disabled?: boolean
    className?: string
}

/**
 * One content field of a message, listed whether or not it is being used.
 *
 * A field the marketer has switched off keeps its value — the switch means
 * "leave this out of the message", not "discard what I wrote" — so turning it
 * back on restores what was there. That is the same `enabled` flag the model
 * has always had; only its presentation is new.
 *
 * Built from plain markup rather than a Well: the per-field toolbars are wide
 * enough to need the whole row, and a Card's header sizes its action slot to
 * its content and will not give them that room.
 */
export const AppMessageContentBlock = ({
    label,
    info,
    enabled,
    onEnabledChange,
    toolbar,
    disabled,
    className,
    children,
}: PropsWithChildren<AppMessageContentBlockProps>) => (
    <div className={getClassNames('app-message-content-block', className, { off: !enabled })}>
        <div className="content-block-head">
            <span className="content-block-name">
                <Switch
                    size="small"
                    checked={enabled}
                    disabled={disabled}
                    onChange={(checked) => onEnabledChange(checked)}
                    aria-label={typeof label === 'string' ? label : undefined}
                />
                <span
                    className="content-block-label"
                    role="presentation"
                    onClick={() => !disabled && onEnabledChange(!enabled)}
                >
                    {label}
                </span>
                {!!info && <InfoTooltip title={info} about={typeof label === 'string' ? label : undefined} />}
            </span>

            {enabled && !!toolbar && <span className="content-block-toolbar">{toolbar}</span>}
        </div>

        {enabled && <div className="content-block-body">{children}</div>}
    </div>
)
